import { Button, Grid, Pagination as PaginationBar, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { BatchTableList } from './BatchTableList';
import FilterIcon from '../../../assets/images/filter-icon.svg';
import LeftArrowWhite from '../../../assets/images/arrow-left-white.svg';
import LeftArrowBlack from '../../../assets/images/arrow-left.svg';
import { useBatchList } from './useBatchList';
import { Batch } from '../../../shared/VolunteerApplicationServiceTypes';
import { BatchSummaryCard } from './BatchSummaryCard';
import { DEFAULT_PAGE_SIZE } from '../../../shared/Constants';
import isEmpty from 'lodash/isEmpty';
import Icon from '../../../components/ui-components/Icon';
import { useCallback, useEffect, useState } from 'react';
import {
	FUNCTIONARY_HOME,
	SCREENING_CREATE_BATCH,
	SCREENING_EDIT_BATCH,
	SCREENING_ELIGIBLE_CANDIDATES,
	SCREENING_VIEW_BATCH,
	SELECT_MODULE,
} from '../../../shared/Routes';
import { useBatchId } from '../../../hooks/useBatchId';
import { useCreateOrViewOrEditBatch } from './CreateOrViewOrEditBatch/useCreateOrViewOrEditBatch';
import { UndoOutlined } from '@ant-design/icons';
import { FilterBatch } from './FilterBatch';
import useUserSession from '../../../hooks/useUserSession';
import { PrivilegeCodes } from '../../../shared/RoleConstants';
import { hasPrivilege } from '../../../shared/Utils';
import EmptyList from '../../../components/EmptyList';

const { Text } = Typography;
const { useBreakpoint } = Grid;

export const BatchListContainer = () => {
	const { t, i18n } = useTranslation();
	const isLengthyLanguage = i18n.language === 'ta' || i18n.language === 'ma';
	const { navigateToRoute } = useBatchId();
	const { loggedInUserPrivileges } = useUserSession();
	const {
		batchData,
		batchPaging,
		onBatchPageChange,
		batchFilter,
		onBatchViewFilterChange,
	} = useBatchList();
	const { addFunctionaryStatus, addOrEditFormStatus } =
		useCreateOrViewOrEditBatch();
	const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
	const [isFilterApplied, setIsFilterApplied] = useState(false);
	const [isDisableCreateButton, setIsDisableCreateButton] =
		useState<boolean>(false);
	const { batches, pagination } = batchData;
	const screens = useBreakpoint();
	const isMobile = !screens.sm;

	useEffect(() => {
		setIsFilterApplied(!isEmpty(batchFilter));
	}, [batchFilter]);

	useEffect(() => {
		const hasDraftedBatch = batches?.some((batch) => {
			return batch.status === 'IN_DRAFT';
		});
		setIsDisableCreateButton(hasDraftedBatch);
	}, [batches]);

	const handleCreateBatchClick = useCallback(() => {
		addFunctionaryStatus();
		navigateToRoute(SCREENING_CREATE_BATCH);
	}, [navigateToRoute, addFunctionaryStatus]);

	const handleEditBatch = useCallback(
		(batchId: number) => {
			sessionStorage.setItem('batchIdEdit', batchId.toString());
			addOrEditFormStatus();
			navigateToRoute(SCREENING_EDIT_BATCH);
		},
		[navigateToRoute, addOrEditFormStatus]
	);

	const handleViewRegisteredCandidate = useCallback(
		(batch: Batch) => {
			sessionStorage.setItem('SELECTED_BATCH', JSON.stringify(batch));
			navigateToRoute(FUNCTIONARY_HOME);
		},
		[navigateToRoute]
	);

	const handleViewBatch = useCallback(
		(batchId: number) => {
			sessionStorage.setItem('batchIdEdit', batchId.toString());
			addOrEditFormStatus();
			navigateToRoute(SCREENING_VIEW_BATCH, {
				state: { from: 'FROM_VIEW_BATCH' },
			});
		},
		[addOrEditFormStatus, navigateToRoute]
	);

	const handleEligibleCandidates = useCallback(() => {
		navigateToRoute(SCREENING_ELIGIBLE_CANDIDATES);
	}, [navigateToRoute]);

	const handleFilterPopup = () => {
		setIsFilterModalOpen(true);
	};

	const handleFilterModalClose = () => {
		setIsFilterModalOpen(false);
	};

	const handleResetFilters = useCallback(() => {
		onBatchViewFilterChange({});
	}, [onBatchViewFilterChange]);

	const handleBackClick = useCallback(() => {
		navigateToRoute(SELECT_MODULE);
	}, [navigateToRoute]);

	const showCreateBatch = hasPrivilege(
		PrivilegeCodes.CREATE_BATCH,
		loggedInUserPrivileges
	);

	const batchCards = (batchData?.batches ?? []).map((batch) => (
		<div className="mb-[16px]" key={batch.id}>
			<BatchSummaryCard
				{...batch}
				onEditClick={handleEditBatch}
				onViewClick={handleViewBatch}
				onViewRegisteredCandidateClick={handleViewRegisteredCandidate}
			/>
		</div>
	));

	return (
		<div className="flex flex-col min-w-[350px] max-w-[480px] sm:min-w-0 sm:max-w-none px-[16px] py-[8px]">
			<Text className="hidden md:block whitespace-nowrap font-sans font-bold text-xl text-secondary-black mb-4">
				{t('batch')}
			</Text>

			<div className="flex justify-between">
				<div className="sm:mt-0 md:mt-[16px] flex gap-4">
					{/* Back Button - Web */}
					<Button
						type="primary"
						className="hidden md:block font-bold text-sm"
						onClick={handleBackClick}
					>
						<span className="flex gap-2">
							<Icon src={LeftArrowWhite} width="w-4" height="h-4" />
							{t('back')}
						</span>
					</Button>

					{/* Back Button - Mobile */}
					<div className="flex gap-3 sm:hidden">
						<a
							onClick={handleBackClick}
							className="relative top-[5px] md:top-[3px] mr-2 font-bold text-sm"
						>
							<img src={LeftArrowBlack} width={'w-4'} height={'h-4'} />
						</a>
						<Text className="whitespace-nowrap font-sans font-bold text-xl text-secondary-black mb-4">
							{t('batch')}
						</Text>
					</div>
				</div>
				<div className="sm:mt-0 md:mt-[16px] flex gap-4 mb-4 sm:justify-start md:justify-end">
					{/* Filter Button - Desktop */}
					<div
						className={`flex ${
							isLengthyLanguage ? 'flex-col-reverse gap-2' : 'flex-row'
						}`}
					>
						{isFilterApplied ? (
							<Button
								className="hidden sm:block flex-1 h-9 text-red-500 font-sans font-bold"
								type="link"
								onClick={handleResetFilters}
							>
								<span>
									{t('resetFilters')}
									<UndoOutlined className="ml-2" />
								</span>
							</Button>
						) : (
							''
						)}
						<Button
							className={`hidden md:block font-sans font-bold text-sm mt-0.5`}
							type="primary"
							onClick={handleFilterPopup}
						>
							{isFilterApplied ? t('filterApplied') : t('filter')}
						</Button>
					</div>

					{/* Filter Icon - Mobile */}
					<Button
						className="block sm:hidden"
						variant="outlined"
						onClick={handleFilterPopup}
					>
						<img src={FilterIcon} alt="Filter Icon" className="w-5 h-5" />
					</Button>
					{/* Add Proposer/Add Functionary - Web */}
					{showCreateBatch && (
						<Button
							type="link"
							className={`hidden md:block text-primary-blue font-sans font-bold text-sm border border-solid mt-0.5 border-primary-blue`}
							variant="outlined"
							onClick={handleCreateBatchClick}
							disabled={isDisableCreateButton}
						>
							{t('createBatch')}
						</Button>
					)}
				</div>
			</div>

			{showCreateBatch && (
				<Button
					onClick={handleCreateBatchClick}
					className="w-full block sm:hidden text-primary-blue font-sans font-bold text-sm border border-solid border-primary-blue my-[16px]"
					disabled={isDisableCreateButton}
				>
					{t('createBatch')}
				</Button>
			)}

			<div className="flex flex-col gap-6">
				{isEmpty(batches) ? (
					<EmptyList message={t('noBatches')} />
				) : isMobile ? (
					<>
						{batchCards}
						<div className="flex justify-center w-full">
							<PaginationBar
								current={batchPaging.pageNo ?? 1}
								total={pagination.totalNoOfRecords}
								showTotal={(total, range) =>
									`${range[0]}-${range[1]} of ${total} items`
								}
								defaultPageSize={batchPaging.pageSize ?? DEFAULT_PAGE_SIZE}
								onChange={onBatchPageChange}
							/>
						</div>
					</>
				) : (
					<BatchTableList
						batches={batches}
						pagination={pagination}
						paging={batchPaging}
						onPageChange={onBatchPageChange}
						handleEditBatch={handleEditBatch}
						handleViewBatch={handleViewBatch}
						handleViewRegisteredCandidate={handleViewRegisteredCandidate}
						handleEligibleCandidates={handleEligibleCandidates}
					/>
				)}
			</div>
			<FilterBatch
				open={isFilterModalOpen}
				onClose={handleFilterModalClose}
				batchValue={batchFilter}
				onBatchFilterChange={onBatchViewFilterChange}
			/>
		</div>
	);
};
